import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { ContactType, IClientContact } from '@bb/contacts';

import errorReporter from '@app/errorReporter';
import APIService, { ContactController, UserController } from 'api';
import { Icon, message, Title } from 'components';

import {
  authorizedLocations,
  isListingAvailable,
  getOrgFeatureMap,
  OrgFeatureMap,
} from 'utils';
import type { GetSimpleListingsByIdsDTO } from '@/app-package';
import DrawerProfile from './DrawerProfile';
import ContactForm from './Form';
import DropdownMenu from './Form/DropdownMenu';

import {
  ContactDrawerMode,
  ContactDrawerType,
  ContactDrawerUser,
  ContactMatchParams,
  isContact,
  isUser,
} from '../types';
import DrawerDetails from './DrawerDetails';
import {
  AuthContext,
  AuthContextType,
  ContactDrawerContext,
  ContactDrawerContextType,
} from '../../../contexts';
import ChangeOwnerModal from './ChangeOwnerModal';
import {
  ProfileContainer,
  ActionHeader,
  Container,
  EditIconContainer,
  ContentContainer,
  GoBackIconContainer,
  DropdownMenuContainer,
} from './Content/common';

interface DrawerContentState {
  contact: ContactDrawerUser | undefined;
  changeOwnerModalVisible: boolean;
  linkedContacts: IClientContact[];
  populatedListings: GetSimpleListingsByIdsDTO['listings'] | undefined;
  loading: boolean;
  permissions: {
    canRead?: boolean;
    canWrite?: boolean;
  };
  contactFeatureMap?: OrgFeatureMap;
  canResendClientInvitation: boolean;
}
interface DrawerContentProps extends RouteComponentProps<ContactMatchParams> {
  parentHistory: any;
  mode: ContactDrawerMode;
  type: ContactDrawerType;
}

interface DrawerContentWithContextsProps extends DrawerContentProps {
  authContextValue: AuthContextType;
  contactDrawerContextValue: ContactDrawerContextType;
}

class DrawerContentWithContexts extends React.Component<
  DrawerContentWithContextsProps,
  DrawerContentState
> {
  public constructor(props: DrawerContentWithContextsProps) {
    super(props);

    this.state = {
      contact: undefined,
      contactFeatureMap: undefined,
      changeOwnerModalVisible: false,
      linkedContacts: [],
      populatedListings: [],
      // Nothing to fetch for coop contacts
      // Contact information is stored as `coopContact` in context
      loading: props.type !== 'coop',
      permissions: {
        canRead: false,
        canWrite: false,
      },
      canResendClientInvitation: false,
    };
  }

  public async componentDidMount() {
    const {
      mode,
      match: { params },
      type,
      history,
      contactDrawerContextValue,
      authContextValue,
    } = this.props;

    const { setContactDrawerMode, agentContact } = contactDrawerContextValue;
    const { auth } = authContextValue;

    history.listen((_, action) => {
      if (action === 'POP') {
        setContactDrawerMode(mode);
      }
    });

    const { id } = params;

    if (['profile', 'edit'].includes(mode) && id && this.state.loading) {
      try {
        if (type === 'user') {
          if (agentContact) {
            // If we have an agent contact, we don't need to fetch the user
            // Used on external dashboard
            this.setState({
              contact: agentContact,
              contactFeatureMap: {} as any,
            });
            return;
          }
          const contact = await UserController.getProfile(id, {
            populateOrg: true,
            populateReps: true,
            populateTeam: true,
            populateUserSubscriptions: true,
          });
          const contactFeatureMap = getOrgFeatureMap(contact.org);
          this.setState({ contact, contactFeatureMap });
        }
        if (type === 'contact') {
          const { contact, permissions } =
            await ContactController.getContactById(id);
          if (contact.type === ContactType.Client) {
            const clientPromises: Promise<any>[] = [];
            if (auth.id === contact.owner.id) {
              clientPromises.push(
                ContactController.findLinkedContacts(contact.id),
              );
            } else {
              clientPromises.push(Promise.resolve(null));
            }

            if (contact.listings?.length) {
              clientPromises.push(
                APIService.GET.getSimpleListings(
                  contact.listings.map(({ id: listingId }) => listingId),
                ),
              );
            } else {
              clientPromises.push(Promise.resolve(null));
            }

            const [linkedContacts, populatedListings] =
              await Promise.all(clientPromises);

            if (linkedContacts) {
              this.setState({ linkedContacts });
            }
            if (populatedListings) {
              this.setState({ populatedListings });
              if (
                auth.id === contact.owner.id &&
                populatedListings.find(isListingAvailable)
              ) {
                this.setState({ canResendClientInvitation: true });
              }
            }
          }

          const { setContactType } = contactDrawerContextValue;
          setContactType(contact.type);

          this.setState({ contact, permissions });
        }
      } catch (err) {
        message.error('Could not fetch contact');
        errorReporter.error(err);
      } finally {
        this.setState({ loading: false });
      }
    }
  }

  public goToContact = (type: ContactDrawerType, contactId: string) => {
    const { history } = this.props;
    history.push(`/${type}/${contactId}`);
  };

  public goToListing = (listingId: string) => {
    const { contactDrawerContextValue, parentHistory } = this.props;

    const { closeContactDrawer } = contactDrawerContextValue;

    closeContactDrawer();
    parentHistory.push(`/listing/${listingId}/view`);
  };

  private _goToEditContact = () => {
    const { contact } = this.state;
    const {
      authContextValue,
      history,
      parentHistory,
      contactDrawerContextValue,
    } = this.props;
    const { auth } = authContextValue;

    const { closeContactDrawer, setContactDrawerMode } =
      contactDrawerContextValue;

    if (isUser(contact)) {
      closeContactDrawer();
      if (auth.id === contact.id) parentHistory.push(`/profile`);
      else parentHistory.push(`/users/edit/${contact.id}`);
    }

    if (isContact(contact)) {
      setContactDrawerMode(ContactDrawerMode.Edit);
      history.push(`/contact/${contact.id}/edit`);
    }
  };

  private _canEdit = () => {
    const { contact, permissions } = this.state;
    const { authContextValue } = this.props;
    const { auth } = authContextValue;

    if (!auth || !contact) return false;

    const { type, mode } = this.props;

    if (mode !== 'profile') return false;

    if (type === 'user' && isUser(contact)) {
      if (auth.id === contact.id) return true;
      if (auth.org.id !== contact.org.id) return false;
      if (auth.roles[auth.role] === auth.roles.BRANCH_ADMIN) {
        return (
          authorizedLocations(auth).includes(contact.location) &&
          auth.roles[contact.role] <= auth.roles.BRANCH_ADMIN
        );
      }
      return auth.roles[auth.role] > auth.roles.BRANCH_ADMIN;
    }

    if (type === 'contact' && isContact(contact)) {
      return permissions.canWrite;
    }

    return false;
  };

  private _goBack = () => {
    const { history } = this.props;

    history.goBack();
  };

  public openChangeOwnerModal = () =>
    this.setState({ changeOwnerModalVisible: true });

  public closeChangeOwnerModal = () =>
    this.setState({ changeOwnerModalVisible: false });

  public archiveContact = async () => {
    const { contactDrawerContextValue } = this.props;
    const { closeContactDrawer, triggerReload } = contactDrawerContextValue;
    try {
      const { contact } = this.state;
      await ContactController.archiveByIds([contact.id]);
      message.success(
        `Archived Contact: ${contact.firstName} ${contact.lastName}`,
      );
      closeContactDrawer();
      triggerReload();
    } catch (err) {
      errorReporter.error(err);
      message.error('Problem archiving contact');
    }
  };

  public resendClientInvitation = async (clientId: string) => {
    const hideMessage = message.loading('Sending invitation to contact', 0);
    try {
      await ContactController.resendClientInvitation(clientId);
      message.success('Successfully sent invitation');
    } catch (err) {
      errorReporter.error(err);
      message.error('Problem resending invitation');
    } finally {
      hideMessage();
    }
  };

  public render() {
    const {
      changeOwnerModalVisible,
      contact,
      linkedContacts,
      populatedListings,
      loading,
      contactFeatureMap,
      canResendClientInvitation,
    } = this.state;
    const { history, mode, type, authContextValue, contactDrawerContextValue } =
      this.props;
    const { contactType, closeContactDrawer } = contactDrawerContextValue;
    const { auth } = authContextValue;

    return (
      <>
        <Container>
          <ActionHeader>
            {/*
            // @ts-ignore */}
            {history.index > 0 ? (
              <GoBackIconContainer
                onClick={this._goBack}
                data-cy="contact-goBack-icon"
              >
                <Icon
                  type="antdLeftOutlined"
                  height={14}
                  width={14}
                  color={'black'}
                />
              </GoBackIconContainer>
            ) : (
              <GoBackIconContainer
                onClick={() => closeContactDrawer()}
                data-cy="contact-close-icon"
              >
                <Icon type="x-alt" width={12} height={12} color="black" />
              </GoBackIconContainer>
            )}
            {['add', 'edit'].includes(mode) && (
              <StyledTitle level={3}>
                {mode} {mode === 'edit' ? contactType : 'Contact'}
              </StyledTitle>
            )}
            {this._canEdit() ? (
              <EditIconContainer
                onClick={() => this._goToEditContact()}
                data-cy="contact-edit-icon"
              >
                <Icon type="edit" height={20} width={20} color="black" />
              </EditIconContainer>
            ) : null}
            {mode === 'edit' &&
              isContact(contact) &&
              auth.id === contact.owner.id &&
              !contact.isDeleted && (
                <DropdownMenuContainer data-cy="contact-ownerEdit-icon">
                  <DropdownMenu
                    openChangeOwnerModal={this.openChangeOwnerModal}
                    deleteContact={this.archiveContact}
                  />
                </DropdownMenuContainer>
              )}
          </ActionHeader>
          <ContentContainer>
            {mode === 'profile' && (
              <>
                <ProfileContainer>
                  <DrawerProfile
                    loading={loading}
                    type={type}
                    contact={contact}
                    contactFeatureMap={contactFeatureMap}
                  />
                </ProfileContainer>
                <DrawerDetails
                  loading={loading}
                  contact={contact}
                  populatedListings={populatedListings}
                  linkedContacts={linkedContacts}
                  canResendClientInvitation={canResendClientInvitation}
                  goToContact={this.goToContact}
                  goToListing={this.goToListing}
                  resendClientInvitation={this.resendClientInvitation}
                  type={type}
                />
              </>
            )}
            {mode === 'edit' && isContact(contact) && (
              <ContactForm
                mode={mode}
                contact={contact}
                onCancel={this._goBack}
              />
            )}
            {mode === 'add' && <ContactForm mode={mode} />}
          </ContentContainer>
        </Container>
        {contact && type === 'contact' && mode === 'edit' && (
          <ChangeOwnerModal
            contactId={contact.id}
            contactName={`${contact.firstName} ${contact.lastName}`.trim()}
            visible={changeOwnerModalVisible}
            onClose={this.closeChangeOwnerModal}
          />
        )}
      </>
    );
  }
}

const DrawerContent: React.FC<DrawerContentProps> = props => (
  <AuthContext.Consumer>
    {authContextValue => (
      <ContactDrawerContext.Consumer>
        {contactDrawerContextValue => (
          <DrawerContentWithContexts
            {...props}
            authContextValue={authContextValue}
            contactDrawerContextValue={contactDrawerContextValue}
          />
        )}
      </ContactDrawerContext.Consumer>
    )}
  </AuthContext.Consumer>
);

export default DrawerContent;

export const StyledTitle = styled(Title)`
  &&& {
    text-transform: capitalize;
    margin: 0;
    margin-left: 26px;
    line-height: 18px;
  }
`;
